import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl';
import { Link } from './Link';
import { normalizeSupportedLocale } from '../utils/routing/locale-from-pathname';
import { TOOLS_TAB_ACTIVE, TOOLS_TAB_INACTIVE } from '../layouts/tools-page-classes';

const messages = defineMessages({
  tools: {
    id: 'tools.title',
    defaultMessage: 'Tools',
    description: 'Tools section title in tool switcher navigation',
  },
  activity: {
    id: 'tools.jobFlow',
    defaultMessage: 'Activity',
    description: 'Activity visualization tool link in tool switcher',
  },
  viewedJobs: {
    id: 'tools.viewedJobs',
    defaultMessage: 'Viewed Jobs',
    description: 'Viewed jobs page link in tool switcher',
  },
  savedJobs: {
    id: 'tools.savedJobs',
    defaultMessage: 'Saved Jobs',
    description: 'Saved jobs page link in tool switcher',
  },
  appliedJobs: {
    id: 'tools.appliedJobs',
    defaultMessage: 'Applied Jobs',
    description: 'Applied jobs page link in tool switcher',
  },
  interviewingJobs: {
    id: 'tools.interviewingJobs',
    defaultMessage: 'Interviewing Jobs',
    description: 'Interviewing jobs page link in tool switcher',
  },
  offerJobs: {
    id: 'tools.offerJobs',
    defaultMessage: 'Offer Jobs',
    description: 'Offer jobs page link in tool switcher',
  },
  rejectedJobs: {
    id: 'tools.rejectedJobs',
    defaultMessage: 'Rejected Jobs',
    description: 'Rejected jobs page link in tool switcher',
  },
  hiredJobs: {
    id: 'tools.hiredJobs',
    defaultMessage: 'Hired Jobs',
    description: 'Hired jobs page link in tool switcher',
  },
  hiddenJobs: {
    id: 'tools.hiddenJobs',
    defaultMessage: 'Hidden Jobs',
    description: 'Hidden jobs page link in tool switcher',
  },
  recentSearches: {
    id: 'tools.recentSearches',
    defaultMessage: 'Saved Searches',
    description: 'Recent searches page link in tool switcher',
  },
});

interface ToolSwitcherProps {
  currentTool: string;
  variant?: 'default' | 'navbar';
}

const ToolSwitcher: React.FC<ToolSwitcherProps> = ({ currentTool, variant = 'default' }) => {
  const intl = useIntl();

  const normalizePath = (path: string) => {
    if (path === '/') return path;
    return path.replace(/\/$/, '');
  };
  const normalizedCurrentTool = normalizePath(currentTool);

  const tools = [
    { route: '/tools/job-flow-sankey', label: intl.formatMessage(messages.activity) },
    { route: '/tools/viewed-jobs', label: intl.formatMessage(messages.viewedJobs) },
    { route: '/tools/saved-jobs', label: intl.formatMessage(messages.savedJobs) },
    { route: '/tools/applied-jobs', label: intl.formatMessage(messages.appliedJobs) },
    { route: '/tools/interviewing-jobs', label: intl.formatMessage(messages.interviewingJobs) },
    { route: '/tools/offer-jobs', label: intl.formatMessage(messages.offerJobs) },
    { route: '/tools/rejected-jobs', label: intl.formatMessage(messages.rejectedJobs) },
    { route: '/tools/hired-jobs', label: intl.formatMessage(messages.hiredJobs) },
    { route: '/tools/hidden-jobs', label: intl.formatMessage(messages.hiddenJobs) },
    { route: '/tools/saved-searches', label: intl.formatMessage(messages.recentSearches) },
  ];

  if (variant === 'navbar') {
    return (
      <div className="flex items-center gap-2 overflow-x-auto flex-1 px-2 scrollbar-hide">
        <span className="text-xs font-medium text-jc-muted whitespace-nowrap flex-shrink-0">
          {intl.formatMessage(messages.tools)}:
        </span>
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
          {tools.map((tool) => {
            const normalizedRoute = normalizePath(tool.route);
            const active = normalizedCurrentTool === normalizedRoute;
            return (
              <Link
                key={tool.route}
                href={tool.route}
                locale={normalizeSupportedLocale(intl.locale)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  active ? TOOLS_TAB_ACTIVE : `${TOOLS_TAB_INACTIVE} border-transparent`
                }`}
              >
                {tool.label}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-jc-surface/95 dark:bg-jc-surface/95 shadow-md border-x border-b border-jc-border rounded-b-2xl md:mt-4 hidden md:block backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-6 pb-1">
            <h2 className="text-lg font-medium text-jc-ink mb-2 text-center">
              {intl.formatMessage(messages.tools)}
            </h2>
            <div className="flex space-x-4 overflow-x-auto justify-start sm:justify-center px-4 scroll-px-4">
              {tools.map((tool) => {
                const normalizedRoute = normalizePath(tool.route);
                const active = normalizedCurrentTool === normalizedRoute;
                return (
                  <Link
                    key={tool.route}
                    href={tool.route}
                    locale={normalizeSupportedLocale(intl.locale)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      active ? TOOLS_TAB_ACTIVE : TOOLS_TAB_INACTIVE
                    }`}
                  >
                    {tool.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <nav
        className="fixed bottom-0 left-0 w-full z-50 flex justify-between items-center px-1 py-1 border-t border-jc-border md:hidden bg-jc-surface/95 text-jc-ink shadow-[0_8px_32px_rgba(30,26,20,0.12)] dark:shadow-none rounded-t-3xl transition-colors duration-300 overflow-hidden backdrop-blur-md"
      >
        {tools.map((tool, idx) => {
          const normalizedRoute = normalizePath(tool.route);
          const active = normalizedCurrentTool === normalizedRoute;
          return (
            <React.Fragment key={tool.route}>
              <Link
                href={tool.route}
                locale={normalizeSupportedLocale(intl.locale)}
                className={`flex-1 flex flex-col items-center min-w-0 py-2 px-0 rounded-xl text-xs font-semibold border transition-colors duration-200 mx-0.5 ${
                  active
                    ? `${TOOLS_TAB_ACTIVE} shadow-sm`
                    : 'text-jc-ink-secondary border border-transparent hover:bg-jc-elevated hover:text-jc-ink'
                }`}
                style={{ minWidth: 0 }}
              >
                <span className="truncate whitespace-normal break-words text-center leading-tight font-semibold text-xs min-h-[2.25rem] leading-[1.1]">
                  {tool.label}
                </span>
              </Link>
              {idx < tools.length - 1 && <div className="w-px h-7 bg-jc-border mx-0.5 flex-shrink-0" />}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
};

export default ToolSwitcher;
